// ─────────────────────────────────────────────────────────────────────────
// Human labels for `charge_type`, in one table.
//
// WHY THIS EXISTS
// There were two maps: Tariffs.tsx's `getChargeTypeName` (terse — "Energy",
// "Reactive") and TariffManagement.tsx's `chargeTypeLabel` (full, with the
// unit — "Energy (kWh)", "Reactive (kVArh)"). Same lookup, different
// coverage: only the second had `water_kl`, so a water tariff rendered as the
// raw key, "water kl", on the schedule screen. Keeping both registers is
// right — they serve different contexts, see below — but keeping them in two
// places is what let one silently fall behind the other. One table, two
// accessors.
//
// THE UNIT TRAP
// `charge_type` is itself unit-asserting, and it lies. A WATER bill's line
// item carries `charge_type: 'energy_kwh'` with `unit: 'kL'` — bill-calculator
// routes water through the `rate_type === 'simple'` path
// (bill-calculator/src/index.ts:1732-1751), which hard-codes the charge type
// and takes the unit from `consumptionUnit`. So a label map keyed on
// `charge_type` alone renders "Energy (kWh)" on a kL line — worse than the raw
// key, because it reads as authoritative.
//
// THE UTILITY TRAP (the same bug, one word further in)
// Dropping the unit was necessary but not sufficient. "Energy" removes the
// UNIT assertion and keeps the UTILITY assertion, and water is not energy.
// Type is a CATEGORY column — its neighbours are Fixed, Service, Network,
// Administration, Chargeable Demand — so it answers "what kind of charge is
// this?", never "what utility is this?". `energy_kwh` is the CONSUMPTION
// charge, and it is that for both utilities: bill-calculator folds a water
// tariff's `water_kl` rate into the energy_kwh slot (:453-457), then emits
// `charge_type: 'energy_kwh'` with `unit: 'kL'` (:1744, :1479). It is the only
// key here reused across utilities, so it is the only one that must be
// utility-neutral.
//
// Do NOT make this map utility-aware ("Energy" for electricity, "Water" for
// water). The Description column two cells to the left already says "Water
// Charge"; repeating the utility adds nothing while the category information
// stays missing.
//
// Hence the split:
//   `chargeTypeName`  — NO unit, and no utility. For rows that print their own
//                       unit alongside (BillDetail's line items render
//                       `item.unit` two columns over; Tariffs' schedule has a
//                       Unit column).
//   `chargeTypeLabel` — WITH unit. Its ONE call site is the tariff editor's
//                       charge-type <select> (TariffManagement.tsx:1143), where
//                       `energy_kwh` and `water_kl` are siblings in one menu.
//                       Strip the utility word from both and the unit is the
//                       only thing left telling them apart, so it stays: here it
//                       disambiguates a menu of OPTIONS rather than asserting
//                       anything about a row of DATA. (The older claim that
//                       "nothing else on screen carries the unit" was simply
//                       wrong — that rate row has its own Unit cell at
//                       TariffManagement.tsx:1128.)
//
// Use `chargeTypeName` anywhere a bill line item is displayed.
// ─────────────────────────────────────────────────────────────────────────

interface ChargeTypeLabels {
  /** Terse, unit-free, utility-free — a category. */
  name: string;
  /** Full, carries the unit. */
  label: string;
}

// Every key both former maps had, unioned. Two entries deliberately do NOT
// preserve what the old maps rendered: `water_kl`, which previously had no
// `name` at all and fell through to the raw key, and `energy_kwh`, which read
// "Energy" / "Energy (kWh)" until a water bill put it beside a kL quantity.
//
// `water_kl` KEEPS its utility name on purpose, and it is not the same defect:
// it is tariff-side only — bill-calculator never emits it as a line-item
// charge type — so it can never label a non-water row, and in the editor's
// <select> it is what distinguishes the water consumption rate from the
// electricity one.
const CHARGE_TYPES: Record<string, ChargeTypeLabels> = {
  energy_kwh: { name: 'Consumption', label: 'Consumption (kWh)' },
  max_demand_kva: { name: 'Chargeable Demand', label: 'Chargeable Demand (kVA)' },
  // legacy — pre-migration-0024 bills
  demand_kva: { name: 'Demand', label: 'Demand (kVA) [legacy]' },
  nmd_kva: { name: 'NMD', label: 'NMD (kVA / R per POD/day)' },
  fixed_monthly: { name: 'Fixed', label: 'Fixed Monthly' },
  service_charge: { name: 'Service', label: 'Service Charge' },
  reactive_kvarh: { name: 'Reactive', label: 'Reactive (kVArh)' },
  network_charge: { name: 'Network', label: 'Network Charge' },
  levy: { name: 'Levy', label: 'Levy' },
  administration_charge: { name: 'Administration', label: 'Administration' },
  network_capacity_charge: { name: 'Network Capacity', label: 'Network Capacity (kVA)' },
  network_demand_charge: { name: 'Network Demand', label: 'Network Demand' },
  transmission_network_charge: { name: 'Transmission Network', label: 'Transmission Network Charge (kVA)' },
  urban_low_voltage_subsidy_charge: { name: 'Urban LV Subsidy', label: 'Urban LV Subsidy (kVA)' },
  excess_network_capacity_charge: { name: 'Excess Network Capacity', label: 'Excess Network Capacity (kVA, per-event)' },
  ancillary_service_charge: { name: 'Ancillary Service', label: 'Ancillary Service' },
  generator_capacity_charge: { name: 'Generator Capacity', label: 'Generator Capacity' },
  legacy_charge: { name: 'Legacy Charge', label: 'Legacy Charge' },
  water_kl: { name: 'Water', label: 'Water (kL)' },
  // CoCT Annexure 6 §60 wording. Water-only by construction: bill-calculator
  // emits these on water bills alone.
  sanitation_kl: { name: 'Sanitation', label: 'Sanitation (% of water, kL)' },
  sanitation_fixed: { name: 'Sewerage Fixed Basic', label: 'Sewerage Fixed Basic' },
};

/** Unknown keys degrade to the humanised key rather than rendering nothing. */
const humanise = (type: string) => type.replace(/_/g, ' ');

/**
 * Terse, category-shaped name — asserts neither a unit nor a utility. Use
 * where the unit is rendered separately: a bill line item, a rate-schedule row
 * with its own Unit column.
 */
export function chargeTypeName(type: string): string {
  return CHARGE_TYPES[type]?.name ?? humanise(type);
}

/**
 * Full name including the unit. For the tariff editor's charge-type <select>,
 * where the unit is what tells `energy_kwh` and `water_kl` apart in one menu.
 * Never use this against a bill line item, whose unit may disagree with it.
 */
export function chargeTypeLabel(type: string): string {
  return CHARGE_TYPES[type]?.label ?? humanise(type);
}

// The unit a rate row should carry once its charge type is chosen. New rows
// default to 'kWh', and a fixed charge saved that way printed "kWh" on bills
// (CoCT SPU 1, 2026-10-08). A unit that already fits the type is kept.
const FIXED_PER_MONTH = new Set(['fixed_monthly', 'service_charge', 'sanitation_fixed']);
const PER_KL = new Set(['water_kl', 'sanitation_kl']);

export function unitForChargeType(chargeType: string, currentUnit: string): string {
  if (FIXED_PER_MONTH.has(chargeType)) return currentUnit === 'day' || currentUnit === 'month' ? currentUnit : 'month';
  // The calculator bills administration_charge per day unless it says month.
  if (chargeType === 'administration_charge') return currentUnit === 'day' || currentUnit === 'month' ? currentUnit : 'day';
  if (PER_KL.has(chargeType)) return currentUnit.toLowerCase() === 'kl' ? currentUnit : 'kL';
  return currentUnit;
}
